<template>
  <v-row class="asc-plot-axes-wrapper--container" no-gutters>
    <template v-if="!isAutoscaleHidden">
      <v-col cols="2">
        <div class="asc-display-flex">
          <asc-switch v-model="autoscale" label="Autoscale"></asc-switch>
          <v-divider class="asc-plot-axes-wrapper--divider" vertical />
        </div>
      </v-col>
    </template>
    <v-col cols="10">
      <template v-if="!isYAxisOverridesHidden">
        <asc-plot-y-axis-overrides
          :overrides="getOverrides"
          @changes="handleOverridesChange"
        />
      </template>
    </v-col>
  </v-row>
</template>

<script>
import AscSwitch from '@/components/atoms/AscSwitch'
import AscPlotYAxisOverrides from '@/components/molecules/AscPlotYAxisOverrides'
import {
  getAxesZoomHiddenGivenPlotTypeId,
  getAxesAutoscaleHiddenGivenPlotTypeId,
  getAxesYAxisOverridesHiddenGivenPlotTypeId,
} from '@/utilities/plotting'
import _ from 'lodash'

export default {
  name: 'AscPlotAxesWrapper',

  components: {
    AscSwitch,
    AscPlotYAxisOverrides,
  },

  props: {
    pagePlot: {
      type: Object,
      default: () => {},
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    activeChip: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      autoscale: this.pagePlot?.options?.axes?.autoscale || false,
    }
  },

  computed: {
    isEmpty() {
      return this.getOverrides.length === 0
    },

    isZoomHidden() {
      return getAxesZoomHiddenGivenPlotTypeId(this.pagePlot.plotTypeId)
    },

    isAutoscaleHidden() {
      return getAxesAutoscaleHiddenGivenPlotTypeId(this.pagePlot.plotTypeId)
    },

    isYAxisOverridesHidden() {
      return getAxesYAxisOverridesHiddenGivenPlotTypeId(
        this.pagePlot.plotTypeId,
      )
    },

    getOverrides() {
      const userOverrides = this.pagePlot?.options?.axes?.overrides || []
      const dataOverrides = this.getDataOverrides

      const groupedUnits = _.groupBy(dataOverrides, 'unit')
      const mappedUnits = []

      const keys = Object.keys(groupedUnits)
      keys.forEach((key, index) => {
        const values = Object.values(groupedUnits)[index]
        const minMaxData = []
        const filteredValues = values.filter(item => !item.tabHidden)
        if (filteredValues.length > 0) {
          filteredValues.forEach(value => {
            minMaxData.push(value.min)
            minMaxData.push(value.max)
          })
          const min = Math.min.apply(null, minMaxData)
          const max = Math.max.apply(null, minMaxData)
          const [override] = userOverrides.filter(ovr => ovr.unit === key)

          const item = {
            unit: key,
            min: override ? override.min : min,
            max: override ? override.max : max,
          }
          mappedUnits.push(item)
        }
      })

      return mappedUnits
    },

    getDataOverrides() {
      if (this.chartData.length > 0) {
        const plotId = this.pagePlot.id
        const data = this.chartData.filter(item => item.plotId === plotId)
        const mappedData = data
          .filter(item => item.plotId === this.pagePlot.id)
          .map(item => {
            return {
              max: item.override?.max || 0,
              min: item.override?.min || 0,
              plotId: item.plotId,
              tabIndex: item.override?.tabIndex || 0,
              tabHidden: item.tabHidden,
              unit: item.override?.unit || '',
            }
          })

        return mappedData
      }

      return []
    },
  },

  watch: {
    autoscale(newValue) {
      this.autoscale = newValue
      this.$emit('autoscaleSelection', newValue)
    },
  },

  methods: {
    handleOverridesChange(override) {
      this.$emit('filterChange', override)
    },

    handleXAxisZoomSelection(chip) {
      this.$emit('chipSelection', chip)
    },
  },
}
</script>

<style lang="scss">
.asc-plot-axes-wrapper {
  &--container {
    height: 90px;
    background-color: transparent;
  }

  &--divider {
    color: $gray-300;
    margin: 0 30px;
    min-height: 90px;
  }
}

.asc-display-flex {
  display: flex;
}
</style>
